import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Button from '../components/ui/Button';
import SearchInput from '../components/ui/SearchInput';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  getMasterCatalogueItems,
  getFilterOptions,
  updateMasterCatalogueKeywords,
  bulkEditMasterCatalogueKeywords,
  deleteMasterCatalogueItem,
  createPriceListFromCatalogue,
  downloadMasterCatalogueExport,
  previewMasterCatalogueImport,
  commitMasterCatalogueImport,
  MasterCatalogueItem,
  MasterCatalogueListResponse,
  ImportPreviewRow,
} from '../lib/masterCatalogueApi';
import {
  listItemGroups,
  addItemGroupMembers,
  removeItemGroupMembers,
  ItemGroup as ItemGroupOption,
} from '../lib/itemGroupApi';
import { Modal } from '../components/Modal';
import { ModuleInfoButton } from '../components/ModuleInfoButton';
import CatalogueSyncBanner from '../features/vendorInvoices/CatalogueSyncBanner';
import LoadingState from '../components/LoadingState';
import { getExchangeRate, getVatRate } from '../lib/settingsApi';
import { getAlternateQuotationPricing } from '../utils/quotationUnitConversions';
// What a row's packaging says, worded by the owner the client price list's UOM
// column already calls (BUSINESS_RULES#item-packaging-columns). Prod USC-BUG-0007
// and 0011 asked to read it on the row instead of opening the item.
import { conversionsForItem, describePackaging } from '../utils/unitConversions';
import { LB_PER_KG, LITRES_PER_GALLON } from '../utils/packConversion';
// The one re-price line (D119.1): the server parks a price that moves past it; this row shows the move red.
import { REPRICE_ASK_ABOVE_PCT, repriceChangePct, movesPastRepriceThreshold } from '../utils/repricePolicy';
import { pricingInvoiceId } from '../utils/pricingInvoice';
import { invoicePath } from '../features/vendorInvoices/invoiceLink';
// Every cost tier this row can honestly state, in TT$, in one pass. Extracted
// from this file so it can be tested, and so the per-pack tier stops reading
// `units_per_case` — the hand-entry column, null on every vendor-invoice row,
// which is why "Per Pack" printed "—" on almost the whole catalogue.
import {
  costPriceTiersTtd,
  // Which unit the Cost Price figure is PER when that is not a case, and whether
  // this row's price is stated in TT$ at all. Both live beside the tiers because
  // the quotation's catalogue search popup asks the same two questions of the
  // same row, and two answers to either is a wrong price on a client quotation.
  baseUnitSuffix,
  catalogueRowIsTtd,
  CostTiersTtd,
  // The hover on a blank Per Pack cell (NEW-8): the blank stays, the cell says why.
  perPackBlankReason,
  // The one owner of "item_price plus VAT when vat_applicable" — used below
  // for the pricing legend's own worked example, which used to spell the
  // same formula out inline (`280.5 * (1 + vatRatePercent / 100)`).
  catalogueRowCostPrice,
  formatCostTierTtd,
  // The "pack read off the item name" derivation this grid always built
  // inline — extracted so the vendor-invoice sync preview card can build the
  // exact same figure off its own `priceFacts` instead of showing nothing
  // (the owner, 2026-09-25). This call is byte-for-byte what used to be here.
  altPricingFor,
} from '../features/catalogue/costPriceTiers';
// The category vocabulary, so the filter offers the list rather than the 19
// spellings the data grew while `category` was a free-text box. Fetched ONCE per
// page — it is a dozen values read by one dropdown.
import { ItemCategory, categoryOptions, legacyCategoryLabel, listItemCategories } from '../lib/itemCategoryApi';
import { formatDate as fmtDate, formatDateTime as fmtDateTime } from "../utils/dateFormat";
// The one owner of "how a money amount is printed" (thousands separators, the
// TT$/US$ label, "—" for missing) — QA retest, 9 Oct 2026, item 54: a few
// spots in this file still built their own `TT$${x.toFixed(2)}` string.
import { formatCurrency } from "../utils/priceUtils";
// The upload's supplier, PICKED from the vendor list (QG-2, D86).
import { VendorSelect, NO_VENDOR, type VendorSelection } from '../components/VendorSelect';
// The form, its validation and its conversion-row rules — including the graph
// consistency check the server refuses a save with — now live in one place and
// are shared with the quotation screen's "add this item to the catalogue"
// dialog. `parseServerError` comes back here because this page still uses it for
// delete and for the import flows, which are not part of the form.
import CatalogueItemForm, { parseServerError, SUPPLIER_REQUIRED_MESSAGE } from '../features/catalogue/CatalogueItemForm';
// Typed keywords, comma-separated, as a list: the row editor, the bulk dialog and the form read them alike.
import { keywordList } from '../features/catalogue/keywordList';
// Last-one-wins for overlapping reads into one piece of state — TECH_DEBT #46's
// owner. Used below for the grid, which issues a fresh request every time any
// filter moves and had no way of telling a stale answer from the current one.
import { useLatestRequest } from '../hooks/useLatestRequest';
// One quotation line's catalogue options — the rows that line could be priced
// from, the one that priced it highlighted (BUSINESS_RULES#quotation-line-catalogue-options).
import { loadQuotationLineOptions } from '../features/catalogue/quotationLineOptions';
import { QuotationLineOptionsBanner } from '../features/catalogue/QuotationLineOptionsBanner';
// Why lines of a searched invoice are not in the grid (BUSINESS_RULES#catalogue-invoice-coverage).
import { InvoiceCoverageNote } from '../features/catalogue/InvoiceCoverageNote';
import type { QuotationLineCatalogueOptions } from '../lib/quotationApi';


// Simple inline SVG icons
const SearchIcon = ({ className = '' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const FilterIcon = ({ className = '' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
  </svg>
);

const ChevronLeftIcon = ({ className = '' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
);

const ChevronRightIcon = ({ className = '' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

const EditIcon = ({ className = '' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);

const TrashIcon = ({ className = '' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const SaveIcon = ({ className = '' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const CancelIcon = ({ className = '' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const InfoIcon = ({ className = '' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const PlusIcon = ({ className = '' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

function formatSourceLabel(source?: string | null): string {
  if (source === 'quotation_historical') return 'Historical Quotation';
  if (source === 'vendor_invoice') return 'Vendor Invoice';
  if (source === 'price_list') return 'Price list';
  if (source === 'manual') return 'Manual';
  if (source === 'unknown') return 'Unknown';
  return source || '—';
}

/**
 * A signed move, "+26%" / "-28%". A figure that is past the line never rounds down onto it: 25.4%
 * reads "+25.4%", not a red "+25%" beside a rule that says more than 25%.
 */
function formatMovePct(pct: number, pastLine: boolean): string {
  const whole = Math.round(pct);
  // Past the line it rounds AWAY from zero, so 25.04 reads 25.1 and never a red 25.0. (The 1e6 round
  // first: 25.3 * 10 is 253.00000000000003 and would ceil to 25.4.)
  const awayFromZero = Math.sign(pct) * (Math.ceil(Math.round(Math.abs(pct) * 10 * 1e6) / 1e6) / 10);
  const text = pastLine && Math.abs(whole) <= REPRICE_ASK_ABOVE_PCT ? awayFromZero.toFixed(1) : String(whole);
  return `${pct > 0 && text !== '0' ? '+' : ''}${text}%`;
}

/**
 * An invoice number that opens the vendor invoice it names (D119.2). The address is made from the
 * invoice's ID (`invoicePath`), never from the printed number; with no id this is just the text.
 * A plain click navigates inside the app, a modified click (new tab) goes through the real href.
 */
function InvoiceRef({ id, children }: { id: string | null; children: React.ReactNode }) {
  const navigate = useNavigate();
  if (!id) return <>{children}</>;
  const href = invoicePath(id);
  return (
    <a
      href={href}
      title="Open this vendor invoice"
      className="text-blue-700 hover:underline"
      onClick={(e) => {
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        navigate(href);
      }}
    >
      {children}
    </a>
  );
}

/**
 * What this row's cost moved from, and anything an invoice is waiting to change
 * it to.
 *
 * Both live in this one narrow column because they are the same question asked
 * at two moments. Until 2026-09-08 a vendor's product grew one catalogue row per
 * invoice LINE, so the previous price was still on screen — on the duplicate row
 * beside this one. Those rows are now merged into one, which is the point, and
 * this is where the price they used to carry went.
 *
 * A PENDING figure is not a price this row holds. It is shown as a proposal,
 * never formatted like the cost beside it, because a supplier's new price that
 * moved more than 25% (`utils/repricePolicy`) — or one that would overwrite a figure a person typed —
 * is parked rather than applied. Typing that exact number into the item form
 * accepts it; typing anything else overrides it. There is no Apply button on
 * purpose: `resolveReprice` is scoped to quotation-harvested rows and stays that
 * way.
 */
function PreviousCostCell({ item }: { item: MasterCatalogueItem }) {
  const prev = item.previousPrice;
  const pending = item.pendingReprice;

  if (!prev?.amount && !pending) return <span className="text-[#CCC]">—</span>;

  const from = [prev?.invoiceNumber, prev?.invoiceDate ? String(prev.invoiceDate) : null]
    .filter(Boolean)
    .join(' · ');

  // The move from the previous price to the price the row holds now (D119.1), only inside ONE
  // currency: a price restated in another currency is not a move. "One currency" is the rule the
  // sync compares with (`catalogueRowIsTtd`, twin of the server's `catalogueRowCurrency`): an
  // unstated currency on a vendor-invoice row is TTD. The previous price was written by the same row.
  const sameCurrency =
    catalogueRowIsTtd({ currency: prev?.currency, source: item.source }) === catalogueRowIsTtd(item);
  const movePct = sameCurrency ? repriceChangePct(prev?.amount, item.item_price) : null;
  const moveRed = movesPastRepriceThreshold(movePct);

  return (
    <div className="flex flex-col leading-[14px]">
      {prev?.amount != null && (
        <span title={from ? `Was ${prev.currency || ''} ${prev.amount} on invoice ${from}` : undefined}>
          {/* Reported in the currency it was INVOICED in, not restated in TT$:
              converting a superseded price at today's rate would print a figure
              no document ever stated. `ceil: false` because this is the figure
              a supplier's invoice already stated, shown as printed. */}
          {formatCurrency(Number(prev.amount), prev.currency ?? null, { ceil: false })}
          {movePct != null && (
            <span
              className={`ml-1 text-xs ${moveRed ? 'text-red-600 font-medium' : 'text-slate-500'}`}
              title={
                moveRed
                  ? `This price moved more than ${REPRICE_ASK_ABOVE_PCT}% from the previous one.`
                  : `The move from the previous price. Past ${REPRICE_ASK_ABOVE_PCT}% it shows red.`
              }
            >
              {formatMovePct(movePct, moveRed)}
            </span>
          )}
        </span>
      )}
      {prev?.amount != null && prev.invoiceId && prev.invoiceNumber && (
        <span className="text-xs">
          <InvoiceRef id={prev.invoiceId}>{prev.invoiceNumber}</InvoiceRef>
        </span>
      )}
      {pending && (
        <span
          className="text-xs text-[#B45309] font-medium"
          title={
            pending.reason === 'hand_edited'
              ? 'A supplier invoice states this price. It was not applied because the current price was typed by hand — enter it in the item form to accept it.'
              : pending.reason === 'fresh_item'
              ? 'A supplier invoice states this price. It was not applied because this is a fresh item, priced by hand monthly — enter it in the item form to accept it.'
              : `A supplier invoice states this price, ${
                  pending.changePct != null
                    ? `a ${formatMovePct(pending.changePct, true)} move. A move of more than ${REPRICE_ASK_ABOVE_PCT}% is not applied`
                    : `a move that cannot be compared with the current price. It was not applied`
                } — enter it in the item form to accept it.`
          }
        >
          {formatCurrency(Number(pending.toPrice), pending.currency ?? null, { ceil: false })} pending
        </span>
      )}
    </div>
  );
}

/**
 * The server's `normalizeName`, for comparing item-group display names.
 * Two groups may answer to the same name — only the slug is unique — so a
 * picker that labels a group by name has to know when the name has stopped
 * identifying one.
 */
function normGroupName(value?: string | null): string {
  return String(value ?? '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, ' ')
    .trim();
}

interface FilterState {
  search: string;
  source?: string;
  category?: string;
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: number;
  /**
   * Assorted-item grouping (ASSORTED_ITEM_GROUPING_PLAN.md).
   * `grouped` collapses each group to the single line the client sees; the
   * default detailed view keeps every vendor variant on its own row.
   */
  grouped?: boolean;
  /** A group id, or 'any' / 'none'. */
  itemGroup?: string;
  /**
   * Only rows where a vendor invoice stated a conversion that CONTRADICTS what
   * the row already says. The sync skips such a row rather than overwriting good
   * data, so this filter is the only way a person ever learns it happened.
   */
  hasConversionConflict?: boolean;
  /** Rows whose Case Packaging states nothing (D29 / MC-5, USC-BUG-0029). */
  needsPackagingReview?: boolean;
  looksLikeOneItem?: boolean;
}

export default function MasterCataloguePage() {
  const [filters, setFilters] = useState<FilterState>({
    search: '',
    page: 1,
    limit: 50,
    sortBy: '_id',
    sortOrder: -1,
  });

  /**
   * What is in the box right now, which is not the same thing as what has been
   * searched for. `filters.search` below is the COMMITTED term — what the grid
   * is showing and what EXPORT exports; this is the keystroke.
   *
   * TECH_DEBT #133. The box used to write straight into `filters.search`, and
   * `filters` is what `fetchItems` re-runs on, so a five-letter term was five
   * un-indexed regex scans of 5,406 rows: asked in order, answered in whatever
   * order the server finished them. The broadest prefix matches the most rows,
   * so it is both the slowest to count and the one that looks like "the filter
   * did nothing" — and it landed last, so it won. The grid settled on the whole
   * catalogue and the search read as dead.
   *
   * The search itself is the SERVER'S and stays there. The grid holds one page
   * of 50 out of 5,406, so filtering what is loaded would answer "No items
   * found" for rows that exist, and would drop the rows the server matched on
   * `keywords` — text this table never displays.
   */
  const [searchInput, setSearchInput] = useState('');

  const [data, setData] = useState<MasterCatalogueListResponse | null>(null);

  /**
   * Opened from a quotation line's catalogue link: `?quotation=<id>&line=<n>`.
   *
   * The grid then holds exactly the rows that line could be priced from, the
   * priced row first and highlighted, instead of a page of the whole catalogue.
   * Search, filters and sorting do not apply to that list — its order is the
   * generator's ranking, which is the point of it — so they are put away until
   * "Show all rows" leaves the mode.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const lineQuotationId = searchParams.get('quotation');
  const lineNumberParam = searchParams.get('line');
  const inLineMode = !!lineQuotationId && !!lineNumberParam;
  const [lineOptions, setLineOptions] = useState<QuotationLineCatalogueOptions | null>(null);
  const [lineOptionsError, setLineOptionsError] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);
  // All catalogue prices are displayed in TT$ only (vendor invoices are in TT$).
  const [exchangeRate, setExchangeRate] = useState<number | null>(null);
  // The one Trinidad VAT rate (owner decision 2026-09-24), admin-set in
  // Settings. Null while it has not loaded or has never been set — a
  // `vat_applicable` row's cost is refused, never guessed, while this is null
  // (`costPriceTiers.catalogueRowCostPrice`).
  const [vatRatePercent, setVatRatePercent] = useState<number | null>(null);
  const [filterOptions, setFilterOptions] = useState<{
    sources: string[];
    categories: (string | null)[];
  }>({ sources: [], categories: [] });
  const [editingKeywords, setEditingKeywords] = useState<{ [key: string]: boolean }>({});
  const [keywordInputs, setKeywordInputs] = useState<{ [key: string]: string }>({});
  const [savingKeywords, setSavingKeywords] = useState<{ [key: string]: boolean }>({});
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [createPriceListOpen, setCreatePriceListOpen] = useState(false);
  const [createPriceListName, setCreatePriceListName] = useState('');
  const [markupPercent, setMarkupPercent] = useState(5);
  const [creatingPriceList, setCreatingPriceList] = useState(false);
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [editItemOpen, setEditItemOpen] = useState<MasterCatalogueItem | null>(null);
  const [exporting, setExporting] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadListName, setUploadListName] = useState('');
  const [uploadSupplier, setUploadSupplier] = useState<VendorSelection>(NO_VENDOR);
  // D107.4: the server's CATALOGUE_SUPPLIER_REQUIRED, shown at the Supplier field and not in the dialog's top alert.
  const [uploadSupplierError, setUploadSupplierError] = useState<string | null>(null);
  // The import flow reports on the page, never in a native alert, which freezes the
  // browser tooling QA tests with (QG-2). What the last import did, and why the dialog failed.
  const [importNotice, setImportNotice] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadingCatalogue, setUploadingCatalogue] = useState(false);
  const [previewingCatalogue, setPreviewingCatalogue] = useState(false);
  const [uploadPreviewRows, setUploadPreviewRows] = useState<ImportPreviewRow[] | null>(null);
  const [uploadSelectedRows, setUploadSelectedRows] = useState<Set<number>>(new Set());
  const uploadFileInputRef = useRef<HTMLInputElement>(null);

  // Assorted-item grouping (ASSORTED_ITEM_GROUPING_PLAN.md) — bulk-assign the
  // selected vendor variants to the one line the client actually orders.
  const [itemGroups, setItemGroups] = useState<ItemGroupOption[]>([]);
  const [assignGroupOpen, setAssignGroupOpen] = useState(false);
  const [assignGroupId, setAssignGroupId] = useState('');
  const [assigningGroup, setAssigningGroup] = useState(false);
  const [groupNotice, setGroupNotice] = useState<string | null>(null);
  // Bulk keyword add and remove on the ticked rows (QG-4). A keyword groups one kind
  // of product across brands on a quotation (BUSINESS_RULES#catalogue-keyword-group).
  const [keywordsOpen, setKeywordsOpen] = useState(false);
  const [keywordsToAdd, setKeywordsToAdd] = useState('');
  const [keywordsToRemove, setKeywordsToRemove] = useState('');
  const [savingBulkKeywords, setSavingBulkKeywords] = useState(false);
  const [keywordsError, setKeywordsError] = useState<string | null>(null);

  // TECH_DEBT #133. Debouncing the box cuts a search down to one request per
  // pause; it cannot order the requests that still overlap — type, pause, type
  // again while the first is in flight — and nothing about a response says
  // which request asked for it. Every other filter on this page (page, sort,
  // source, group) fires immediately and races the same way.
  const beginItemsRequest = useLatestRequest();

  // Fetch master catalogue items
  const fetchItems = useCallback(async () => {
    const isCurrent = beginItemsRequest();
    setLoading(true);
    try {
      if (lineQuotationId && lineNumberParam) {
        const { options, rows } = await loadQuotationLineOptions(lineQuotationId, lineNumberParam);
        if (!isCurrent()) return;
        setLineOptions(options);
        setLineOptionsError(null);
        setData({
          success: true,
          data: rows,
          pagination: { page: 1, limit: Math.max(rows.length, 1), total: rows.length, totalPages: 1 },
        });
        return;
      }
      setLineOptions(null);
      setLineOptionsError(null);
      const result = await getMasterCatalogueItems({
        page: filters.page,
        limit: filters.limit,
        search: filters.search,
        source: filters.source,
        category: filters.category,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
        grouped: filters.grouped,
        itemGroup: filters.itemGroup,
        hasConversionConflict: filters.hasConversionConflict,
        needsPackagingReview: filters.needsPackagingReview,
        looksLikeOneItem: filters.looksLikeOneItem,
      });
      if (!isCurrent()) return;
      setData(result);
    } catch (error) {
      if (!isCurrent()) return;
      if (lineQuotationId && lineNumberParam) {
        // Said on the banner, and the grid emptied: rows from an earlier answer
        // must not sit under a banner that could not load this one.
        setLineOptions(null);
        setLineOptionsError(error);
        setData({ success: true, data: [], pagination: { page: 1, limit: 1, total: 0, totalPages: 1 } });
      }
      console.error('Error fetching master catalogue:', error);
    } finally {
      // Not unconditional: a superseded response switching the spinner off
      // switches it off for the request that is still running, and the grid
      // then shows the previous page's rows as if they were the answer.
      if (isCurrent()) setLoading(false);
    }
  }, [filters, beginItemsRequest, lineQuotationId, lineNumberParam]);

  // Fetch filter options
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const result = await getFilterOptions();
        setFilterOptions({
          sources: result.data.sources,
          categories: result.data.categories.filter(Boolean),
        });
      } catch (error) {
        console.error('Error fetching filter options:', error);
      }
    };
    loadFilterOptions();
  }, []);

  /**
   * The category vocabulary — the list an admin maintains under
   * Settings → Item Categories.
   *
   * Loaded once for the page, not per row and not per keystroke: it is a dozen
   * values that change when somebody edits them in Settings, which is not
   * something this screen does.
   */
  const [categoryVocabulary, setCategoryVocabulary] = useState<ItemCategory[]>([]);
  useEffect(() => {
    listItemCategories()
      .then((res) => setCategoryVocabulary(res.data?.categories || []))
      .catch((e) => console.error('Error fetching item categories:', e));
  }, []);

  /**
   * What the "All Categories" dropdown offers: the vocabulary FIRST, with its
   * readable labels, then every other value the catalogue still holds.
   *
   * The second half is not decoration. The live filter held 19 values —
   * `Electronic` / `Electronics` / `electronics`, `Provision` / `provisions`,
   * the literal string `Null`, `acd`, `asd`, `asdasd`, `asdjn as`, `erer` —
   * and those rows are still filed under them. Dropping them from the filter
   * would make those rows unreachable and make the mess invisible at the same
   * time, which is the opposite of fixing it. They are listed, marked, and stay
   * until somebody re-files the rows; nothing here rewrites data.
   */
  const categoryFilterOptions = useMemo(
    () => categoryOptions(categoryVocabulary, filterOptions.categories),
    [categoryVocabulary, filterOptions.categories]
  );

  /**
   * What a row's Category cell prints. `deck_stores` is the STORED value and
   * the thing a filter matches on; "Deck Stores" is the only thing a person
   * should ever have to read. A value the vocabulary does not know is shown
   * verbatim (tidied for capitalisation), never blanked — a row is not
   * corrected by hiding what it says.
   */
  const categoryLabels = useMemo(
    () => new Map(categoryVocabulary.map((c) => [c.key, c.label])),
    [categoryVocabulary]
  );
  const categoryLabelFor = (value?: string | null): string => {
    const raw = String(value ?? '').trim();
    if (!raw) return '—';
    return categoryLabels.get(raw) || legacyCategoryLabel(raw);
  };

  // Assorted-item groups, for the filter dropdown and the bulk-assign modal.
  const loadItemGroups = useCallback(async () => {
    try {
      const res = await listItemGroups();
      setItemGroups(res.data || []);
    } catch (error) {
      console.error('Error fetching item groups:', error);
    }
  }, []);

  useEffect(() => {
    loadItemGroups();
  }, [loadItemGroups]);

  /**
   * Group names carried by more than one group.
   *
   * Only a group's slug is unique — two can both be called "Assorted Yoghurt" —
   * so anywhere below that labels a group by name alone has to fall back to the
   * slug once the name has stopped identifying one group.
   */
  const duplicateGroupNames = useMemo(() => {
    const seen = new Set<string>();
    const dupes = new Set<string>();
    itemGroups.forEach((g) => {
      const key = normGroupName(g.name);
      if (!key) return;
      if (seen.has(key)) dupes.add(key);
      seen.add(key);
    });
    return dupes;
  }, [itemGroups]);

  const isDuplicateGroupName = useCallback(
    (name?: string | null) => duplicateGroupNames.has(normGroupName(name)),
    [duplicateGroupNames]
  );


  // Fetch exchange rate from user settings (needed to convert USD-stored rows to TT$)
  useEffect(() => {
    const loadExchangeRate = async () => {
      try {
        const rateData = await getExchangeRate();
        // Use the USD to TTD rate (e.g., 6 means 1 USD = 6 TTD)
        setExchangeRate(rateData.usdToTtd);
      } catch (error) {
        console.error('Error fetching exchange rate:', error);
        setExchangeRate(null);
      }
    };
    loadExchangeRate();
  }, []);

  // Fetch the Settings VAT rate (needed to gross a `vat_applicable` row's cost —
  // never a hardcoded 12.5%, per the 2026-09-24 owner decision).
  useEffect(() => {
    const loadVatRate = async () => {
      try {
        const rateData = await getVatRate();
        setVatRatePercent(rateData.ratePercent);
      } catch (error) {
        console.error('Error fetching VAT rate:', error);
        setVatRatePercent(null);
      }
    };
    loadVatRate();
  }, []);

  // Fetch items when filters change
  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleSearch = (value: string) => {
    setSearchInput(value);
  };

  /**
   * Commit the typed term after a pause — 300ms, the same pause the catalogue's
   * other search box (`MasterCatalogueSearchPopup`) has always used.
   *
   * `prev` is returned unchanged when the term has not actually moved (typed
   * and deleted inside one pause, or the mount's own run against the initial
   * empty string), so `filters` keeps its identity and the debounce cannot
   * manufacture a refetch of its own.
   */
  useEffect(() => {
    const t = setTimeout(() => {
      setFilters((prev) =>
        prev.search === searchInput ? prev : { ...prev, search: searchInput, page: 1 }
      );
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const handleSourceFilter = (value: string) => {
    setFilters((prev) => ({
      ...prev,
      source: value || undefined,
      page: 1,
    }));
  };

  const handleCategoryFilter = (value: string) => {
    setFilters((prev) => ({
      ...prev,
      category: value || undefined,
      page: 1,
    }));
  };

  const handleSort = (column: string) => {
    // One line's options are in the generator's order, which is the point of them.
    if (inLineMode) return;
    setFilters((prev) => ({
      ...prev,
      sortBy: column,
      sortOrder: prev.sortBy === column ? -prev.sortOrder : -1,
      page: 1,
    }));
  };

  const handlePageChange = (newPage: number) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
    window.scrollTo(0, 0);
  };

  // No guessed rate. Until the settings rate has loaded (or if it failed to), a
  // USD figure has NO TT$ value — it used to be converted at a hard-coded 6 and
  // shown as if it were a price. `null` here means "—" everywhere below.
  const usdToTtd: number | null = exchangeRate && exchangeRate > 0 ? exchangeRate : null;

  // The owner's printer, shared with the vendor-invoice sync preview card.
  const formatTtd = formatCostTierTtd;

  /**
   * A "Per kg / L" or "Per lb / gal" cell (MC-2): every figure the row states,
   * each with the unit it is per, or "—". `costPriceTiersTtd` refuses the other
   * dimension's pair on a row whose size reads as mass or volume, except an ounce
   * size, which states both (D170.4); else both only on a row with no readable size.
   */
  const measureFigures = (figures: Array<[number | null, string]>) => {
    const shown = figures.filter((f): f is [number, string] => f[0] != null);
    if (shown.length === 0) return <span className="text-[#CCC]">—</span>;
    return shown.map(([v, unit]) => (
      <span key={unit}>
        {formatTtd(v)}
        <span className="text-[#888]"> / {unit}</span>
      </span>
    ));
  };

  /**
   * A price stored on SOME OTHER catalogue row, printed in TT$.
   *
   * Whether that stored figure already IS TT$ is `catalogueRowIsTtd`'s question,
   * and answering it needs the row's SOURCE as well as its currency — which is
   * why the import preview now carries `existingSource` beside the other two.
   *
   * The local `toTtd` this replaces asked `currency === 'TTD'` alone. That is a
   * live duplicate of a rule with an owner, and wrong in exactly the direction
   * the owner documents: `MasterCatalogue.currency` is `default: null`, so on a
   * legacy vendor-invoice row this told the user the item was already in the
   * catalogue at 6.5x the price it is actually in the catalogue at.
   */
  const formatStoredTtd = (
    price?: number | null,
    currency?: string | null,
    source?: string | null
  ): string => {
    if (price == null || !Number.isFinite(price)) return '—';
    if (catalogueRowIsTtd({ currency, source })) return formatTtd(price);
    return usdToTtd == null ? '—' : formatTtd(price * usdToTtd);
  };

  const formatDate = (date?: string) => {
    return fmtDateTime(date, '—');
  };

  /**
   * Every cost tier for one row, in ONE pass — see `features/catalogue/costPriceTiers`.
   *
   * The four cells that used to call a per-unit function four times now share a
   * case figure, a pack count and one parse of the pack size. A tier the row
   * cannot state comes back null and the cell prints "—", which is the honest
   * answer: dividing by a pack count nobody stated would print the CASE price
   * under a "Per Pack" heading.
   */
  const costTiersFor = (
    item: MasterCatalogueItem,
    altPricing: ReturnType<typeof getAlternateQuotationPricing> | null
  ): CostTiersTtd => costPriceTiersTtd(item, usdToTtd, altPricing, vatRatePercent);

  /**
   * Words for the live Settings VAT rate, for the two "12.5%" texts below that
   * used to state the rate as a literal. Rate-neutral wording while the rate
   * has not loaded — never a guessed number.
   */
  const vatRateLabel =
    typeof vatRatePercent === 'number' && Number.isFinite(vatRatePercent)
      ? `${vatRatePercent}%`
      : 'the Settings VAT rate';

  /**
   * The pricing legend's own worked example ("an invoice line at TT$280.50
   * shows here as TT$X at Y%") — computed from the loaded rate through the
   * one owner (`ceilPrice`), never a hardcoded figure. The literal this
   * replaced ("TT$315.56 at 12.5%") was itself a cent short of what
   * `ceilPrice` actually gives for 280.50 x 1.125 (315.5625 ceils to 315.57,
   * not .56) — found fixing this. Dropped entirely, not shown against a
   * guessed rate, while the Settings rate has not loaded.
   */
  const vatWorkedExampleClause =
    typeof vatRatePercent === 'number' && Number.isFinite(vatRatePercent)
      ? ` — an invoice line at TT$280.50 shows here as ${formatTtd(
          catalogueRowCostPrice({ item_price: 280.5, vat_applicable: true }, vatRatePercent)
        )} at ${vatRatePercent}%.`
      : '.';

  /**
   * EXPORT. Ticked rows win; with nothing ticked it exports the filtered view.
   *
   * USC-BUG-0011: the selection was not sent at all, so ticking a handful of
   * rows and pressing EXPORT downloaded the whole catalogue — 5,451 rows — with
   * nothing on screen saying the ticks had been ignored. The other two bulk
   * actions on this page already act on `selectedIds`; this one now agrees with
   * them, and the button label below says which of the two it will do.
   */
  const handleExport = useCallback(async () => {
    // Showing one line's options, EXPORT means those rows. With nothing ticked
    // the export is otherwise "every row matching the filters" — here the whole
    // catalogue, which is not what the screen is showing.
    const shownIds = inLineMode ? (data?.data || []).map((row) => row._id) : [];
    if (inLineMode && selectedIds.size === 0 && shownIds.length === 0) {
      alert('There are no rows to export.');
      return;
    }
    setExporting(true);
    try {
      const ids = selectedIds.size ? Array.from(selectedIds) : shownIds;
      await downloadMasterCatalogueExport({
        search: filters.search || undefined,
        source: filters.source || undefined,
        category: filters.category || undefined,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
        ids,
      });
    } catch (e) {
      console.error('Export failed:', e);
      alert('Export failed.');
    } finally {
      setExporting(false);
    }
  }, [filters.search, filters.source, filters.category, filters.sortBy, filters.sortOrder, selectedIds, inLineMode, data]);

  const resetUploadModal = () => {
    setUploadModalOpen(false);
    setUploadListName('');
    setUploadSupplier(NO_VENDOR);
    setUploadSupplierError(null);
    setUploadError(null);
    setUploadFile(null);
    setUploadPreviewRows(null);
    setUploadSelectedRows(new Set());
    if (uploadFileInputRef.current) uploadFileInputRef.current.value = '';
  };

  /** Step 1: parse the file and show rows so the user can pick which entries to add. */
  const handlePreviewCatalogueImport = async () => {
    if (!uploadFile) {
      alert('Please choose a file (Excel, EML, or image).');
      return;
    }
    setPreviewingCatalogue(true);
    setUploadError(null);
    try {
      const res = await previewMasterCatalogueImport(uploadFile);
      const rows = res.data?.rows || [];
      setUploadPreviewRows(rows);
      // Pre-select only the rows that are not already in the catalogue
      setUploadSelectedRows(new Set(rows.filter(r => !r.duplicate).map(r => r.index)));
    } catch (e: unknown) {
      console.error(e);
      const msg = e instanceof Error ? e.message : 'Could not read the file.';
      setUploadError(msg);
    } finally {
      setPreviewingCatalogue(false);
    }
  };

  const toggleUploadRow = (index: number) => {
    setUploadSelectedRows(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index); else next.add(index);
      return next;
    });
  };

  /** Step 2: import only the selected rows. */
  const handleCommitCatalogueImport = async () => {
    if (!uploadPreviewRows || uploadSelectedRows.size === 0) return;
    setUploadingCatalogue(true);
    setUploadError(null);
    setUploadSupplierError(null);
    try {
      const selected = uploadPreviewRows
        .filter(r => uploadSelectedRows.has(r.index))
        // `packaging` is the unit the file stated beside the price. It is the
        // last of the four fixed field lists that were dropping it between the
        // parser and the writer — which is why every price-list row on prod has
        // a blank unit, and so no per-unit price at all.
        .map(r => ({
          description: r.description,
          unit_price: r.unit_price,
          vat: r.vat,
          packaging: r.packaging,
          // The file's own columns (USC-BUG-0020) — dropped here, they never arrive.
          category: r.category,
          sub_category: r.sub_category,
          vendorName: r.vendorName,
        }));
      const res = await commitMasterCatalogueImport({
        priceListName:
          uploadListName.trim() || uploadFile?.name.replace(/\.[^.]+$/, '') || undefined,
        supplier: uploadSupplier.name.trim() || undefined,
        vendorId: uploadSupplier.vendorId || undefined,
        items: selected,
      });
      setImportNotice(res.message || 'Import completed.');
      resetUploadModal();
      const fo = await getFilterOptions();
      setFilterOptions({
        sources: fo.data.sources,
        categories: fo.data.categories.filter(Boolean),
      });
      fetchItems();
    } catch (e: unknown) {
      console.error(e);
      // D107.4: a row with no vendor of its own and none picked here. The code says so, never the prose.
      if ((e as { code?: unknown } | null)?.code === 'CATALOGUE_SUPPLIER_REQUIRED') {
        setUploadSupplierError(SUPPLIER_REQUIRED_MESSAGE);
        return;
      }
      const msg = e instanceof Error ? e.message : 'Import failed.';
      setUploadError(msg);
    } finally {
      setUploadingCatalogue(false);
    }
  };

  const handleEditKeywords = (itemId: string, currentKeywords: string[] = []) => {
    setEditingKeywords(prev => ({ ...prev, [itemId]: true }));
    setKeywordInputs(prev => ({ ...prev, [itemId]: currentKeywords.join(', ') }));
  };

  const handleCancelEditKeywords = (itemId: string) => {
    setEditingKeywords(prev => ({ ...prev, [itemId]: false }));
    setKeywordInputs(prev => ({ ...prev, [itemId]: '' }));
  };

  const handleSaveKeywords = async (itemId: string) => {
    const keywords = keywordList(keywordInputs[itemId] || '');

    setSavingKeywords(prev => ({ ...prev, [itemId]: true }));

    try {
      await updateMasterCatalogueKeywords(itemId, keywords);
      
      // Update the local data
      setData(prevData => {
        if (!prevData) return prevData;
        return {
          ...prevData,
          data: prevData.data.map(item => 
            item._id === itemId 
              ? { ...item, keywords }
              : item
          )
        };
      });

      setEditingKeywords(prev => ({ ...prev, [itemId]: false }));
      setKeywordInputs(prev => ({ ...prev, [itemId]: '' }));
    } catch (error) {
      console.error('Error updating keywords:', error);
      alert('Failed to update keywords. Please try again.');
    } finally {
      setSavingKeywords(prev => ({ ...prev, [itemId]: false }));
    }
  };

  const SortableHeader = ({
    label,
    column,
    count,
    widthClass,
  }: {
    label: string;
    column: string;
    count?: number;
    widthClass?: string;
  }) => {
    const isActive = filters.sortBy === column;
    const isAsc = filters.sortOrder === 1;
    return (
    <button
      onClick={() => handleSort(column)}
      className={`flex items-center justify-between gap-2 w-full min-h-[24px] px-2 py-0 text-left hover:opacity-80 transition text-xs font-medium leading-[18px] ${widthClass ?? ''}`}
      title={`Sort by ${label}`}
    >
      <span className="flex items-center gap-1.5 text-xs font-normal leading-[18px] text-slate-900">
        {label}
        {count != null && (
          <span className="text-blue-700 font-normal">{count.toLocaleString()}</span>
        )}
      </span>
      <span className="flex items-center gap-0.5 shrink-0">
        <FilterIcon className={`w-4 h-4 ${isActive ? 'text-primary-500' : 'text-slate-500'}`} />
        <span className={`text-xs leading-none ${isActive ? 'text-slate-900' : 'text-[#9CA3AF]'}`}>
          {isActive ? (isAsc ? '▲' : '▼') : '↕'}
        </span>
      </span>
    </button>
    );
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAllOnPage = () => {
    if (!data?.data?.length) return;
    const allSelected = data.data.every(item => selectedIds.has(item._id));
    if (allSelected) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        data.data.forEach(item => next.delete(item._id));
        return next;
      });
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        data.data.forEach(item => next.add(item._id));
        return next;
      });
    }
  };
  /**
   * Bulk-assign the selected catalogue rows to an assorted-item group, or pull
   * them out of one. Both are manual decisions, so the backend locks them —
   * re-ingesting the same vendor invoice will not undo the choice.
   */
  const closeBulkKeywords = () => {
    setKeywordsOpen(false);
    setKeywordsToAdd('');
    setKeywordsToRemove('');
    setKeywordsError(null);
  };

  const handleBulkKeywords = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    setSavingBulkKeywords(true);
    setKeywordsError(null);
    try {
      const res = await bulkEditMasterCatalogueKeywords(ids, keywordList(keywordsToAdd), keywordList(keywordsToRemove));
      const missing = res.data.missing ? ` — ${res.data.missing} ticked item(s) no longer exist` : '';
      setGroupNotice(`${res.message}${missing}`);
      closeBulkKeywords();
      setSelectedIds(new Set());
      await fetchItems();
    } catch (error) {
      // Shown in the dialog, never in a native alert, and the rows stay ticked to try again.
      setKeywordsError(parseServerError(error).message);
    } finally {
      setSavingBulkKeywords(false);
    }
  };

  const handleAssignGroup = async (remove = false) => {
    const ids = Array.from(selectedIds);
    if (!ids.length || (!remove && !assignGroupId)) return;
    setAssigningGroup(true);
    setGroupNotice(null);
    try {
      if (remove) {
        // Removal needs a group to remove from; every selected row that is in a
        // group is handled by its own group id.
        const byGroup = new Map<string, string[]>();
        (data?.data || [])
          .filter((it) => ids.includes(it._id) && it.itemGroupId)
          .forEach((it) => {
            const gid = String(it.itemGroupId);
            byGroup.set(gid, [...(byGroup.get(gid) || []), it._id]);
          });
        if (!byGroup.size) {
          setGroupNotice('None of the selected items are in a group.');
          return;
        }
        let removed = 0;
        for (const [gid, memberIds] of Array.from(byGroup.entries())) {
          // eslint-disable-next-line no-await-in-loop
          const res = await removeItemGroupMembers(gid, memberIds);
          removed += res.data.removed;
        }
        setGroupNotice(`${removed} item(s) removed from their group`);
      } else {
        const res = await addItemGroupMembers(assignGroupId, ids);
        const warned = res.data.warnings?.length
          ? ` — check unit warnings on ${res.data.warnings.length} item(s)`
          : '';
        setGroupNotice(`${res.message}${warned}`);
      }
      setAssignGroupOpen(false);
      setSelectedIds(new Set());
      await Promise.all([fetchItems(), loadItemGroups()]);
    } catch (error) {
      setGroupNotice(parseServerError(error).message);
    } finally {
      setAssigningGroup(false);
    }
  };

  const navigate = useNavigate();
  const handleCreatePriceList = async () => {
    if (!createPriceListName.trim() || selectedIds.size === 0) return;
    setCreatingPriceList(true);
    try {
      const res = await createPriceListFromCatalogue(
        createPriceListName.trim(),
        Array.from(selectedIds),
        markupPercent
      );
      setCreatePriceListOpen(false);
      setCreatePriceListName('');
      setMarkupPercent(5);
      setSelectedIds(new Set());
      const priceListId = res?.data?.priceListId;
      if (priceListId) {
        navigate(`/client-price-lists?openId=${priceListId}`);
      } else {
        alert('Price list created successfully.');
      }
    } catch (e) {
      console.error(e);
      alert('Failed to create price list.');
    } finally {
      setCreatingPriceList(false);
    }
  };


  // Delete a catalogue item (QA Ticket 3). Confirm first — this is irreversible.
  const [deletingItemId, setDeletingItemId] = useState<string | null>(null);
  const handleDeleteItem = async (item: MasterCatalogueItem) => {
    if (!window.confirm(`Delete "${item.item_name}" from the master catalogue? This cannot be undone.`)) return;
    setDeletingItemId(item._id);
    try {
      await deleteMasterCatalogueItem(item._id);
      fetchItems();
    } catch (e) {
      console.error(e);
      alert(parseServerError(e).message || 'Failed to delete item.');
    } finally {
      setDeletingItemId(null);
    }
  };

  // D107.4: the upload dialog's vendor. The server refuses per ROW: a row needs its own registered
  // vendor in the file's Vendor column OR the one picked here, so this is required only when the
  // file does not name them. Shown in both steps: a refusal at Import is fixed in place, rows kept.
  const uploadSupplierField = (
    <div>
      <VendorSelect
        label="Supplier / Vendor"
        value={uploadSupplier}
        onChange={(v) => {
          setUploadSupplier(v);
          setUploadSupplierError(null);
        }}
        disabled={previewingCatalogue || uploadingCatalogue}
      />
      <p className="mt-1 text-xs text-slate-500">
        Required unless every row of the file names its vendor in a Vendor column.
      </p>
      {uploadSupplierError && (
        <p role="alert" className="mt-1 text-xs text-red-600">{uploadSupplierError}</p>
      )}
    </div>
  );

  return (
    <div className="w-full max-w-full overflow-x-auto bg-brand-bg">
      {importNotice && (
        <div
          role="status"
          className="flex items-start justify-between gap-3 px-4 py-2 sm:px-8 text-sm bg-emerald-50 text-emerald-900 border-b border-emerald-200"
        >
          <span>{importNotice}</span>
          <button type="button" onClick={() => setImportNotice(null)} className="text-xs underline shrink-0">
            Dismiss
          </button>
        </div>
      )}
      {/* QA USC-BUG-0061: the last vendor-invoice sync's result, until dismissed. */}
      <div className="px-4 pt-3 sm:px-8 empty:hidden">
        <CatalogueSyncBanner invoiceId={null} />
      </div>
      {/* Header - single row: Title + info, Search, EXPORT, UPLOAD, + PRICE LIST, + ITEM */}
      <div className="sticky top-0 z-10 border-b border-slate-200 bg-brand-bg">
        <div className="flex flex-wrap items-center gap-3 px-4 pb-3 pt-4 sm:px-8">
          <div className="flex items-center gap-2 shrink-0">
            <h1 className="text-xl font-semibold leading-7 text-slate-900">Master Catalog</h1>
            <ModuleInfoButton
              content={
                <div className="flex flex-col gap-3">
                  <p>The Master Catalog is the central list of items used across the system. All prices are in TT$.</p>
                  <p className="font-medium">You can:</p>
                  <ul className="list-disc list-inside space-y-1 pl-1">
                    <li>Search and filter items by source, category, or keywords</li>
                    <li>Create price lists from selected catalogue items</li>
                    <li>Add new items or edit existing ones</li>
                    <li>Export or upload catalogue data</li>
                  </ul>
                  <p className="font-medium">Correcting errors (wrong price, wrong VAT):</p>
                  <ul className="list-disc list-inside space-y-1 pl-1">
                    <li>
                      Edit the entry directly here using the pencil icon next to the item name —
                      fix the price, VAT flag, unit, or supplier. No need to go back to the Vendor
                      Invoice module for a catalog-only fix.
                    </li>
                    <li>
                      <span className="font-medium">Caution:</span> for items sourced from a vendor
                      invoice, re-processing that same invoice will overwrite your direct edit. If
                      the source invoice itself was extracted wrong, correct it in the Vendor
                      Invoice module so the fix sticks.
                    </li>
                    <li>
                      Existing quotes and POs are <span className="font-medium">not</span> changed
                      by catalog corrections — they keep the price they were created with. Only new
                      quotes pick up the corrected price.
                    </li>
                  </ul>
                  <p>Item pricing and details from the Master Catalog can be used when generating quotations.</p>
                </div>
              }
              ariaLabel="Master Catalogue module information"
            />
          </div>
          {/* The keystroke, not the committed term: bind this to `filters.search` and the box drops characters
              typed during the 300ms pause. */}
          <SearchInput
            value={searchInput}
            onChange={handleSearch}
            placeholder="Search item, keyword or invoice number"
            disabled={inLineMode}
            title={inLineMode ? 'Showing one quotation line’s options — use “Show all rows” to search the catalogue' : undefined}
            className="flex-1 min-w-[200px] max-w-md"
          />
          <div className="flex items-center gap-2 flex-wrap ml-auto shrink-0">
            <Button
              onClick={handleExport}
              disabled={exporting}
              // Say which of the two exports this is before it runs. The whole
              // of USC-BUG-0011 was finding out afterwards, by scrolling a file.
              title={
                selectedIds.size
                  ? `Export the ${selectedIds.size} selected item(s)`
                  : inLineMode
                  ? 'Export the rows shown for this quotation line'
                  : 'Export every item matching the current filters'
              }
            >
              {exporting ? 'Exporting...' : selectedIds.size ? `Export (${selectedIds.size})` : 'Export'}
            </Button>
            <Button
              onClick={() => {
                setUploadModalOpen(true);
                setUploadListName('');
                setUploadFile(null);
              }}
            >
              Upload
            </Button>
            <Button
              onClick={() => {
                setAssignGroupId(itemGroups[0]?._id || '');
                setAssignGroupOpen(true);
              }}
              disabled={selectedIds.size === 0 || !itemGroups.length}
              title={
                itemGroups.length
                  ? 'Group the selected vendor variants into one client-facing item'
                  : 'Create an assorted item group first'
              }
            >
              Group
            </Button>
            <Button
              onClick={() => {
                setKeywordsError(null);
                setKeywordsOpen(true);
              }}
              disabled={selectedIds.size === 0}
              title="Add or remove keywords on the selected items. A keyword groups one kind of product across brands on a quotation."
            >
              {selectedIds.size ? `Keywords (${selectedIds.size})` : 'Keywords'}
            </Button>
            <Button
              onClick={() => setCreatePriceListOpen(true)}
              disabled={selectedIds.size === 0}
              icon={<PlusIcon className="w-4 h-4" />}
            >
              Price list
            </Button>
            <Button variant="primary" onClick={() => setAddItemOpen(true)} icon={<PlusIcon className="w-4 h-4" />}>
              Item
            </Button>
          </div>
        </div>

        {/* Filters + Currency + Per page — put away while showing one line's options */}
        {!inLineMode && (
        <div className="px-6 py-2 border-t border-[#D7DBD9]/50 bg-[#F9FAF9] flex flex-wrap items-center gap-4">
          <select
            value={filters.source || ''}
            onChange={(e) => handleSourceFilter(e.target.value)}
            className="px-3 py-1.5 text-sm border border-[#D7DBD9] rounded-md bg-white text-slate-900 focus:outline-none focus:ring-1 focus:ring-[#D7DBD9]"
          >
            <option value="">All Sources</option>
            {filterOptions.sources.map((source) => (
              <option key={source} value={source}>
                {formatSourceLabel(source)}
              </option>
            ))}
          </select>
          <select
            value={filters.category || ''}
            onChange={(e) => handleCategoryFilter(e.target.value)}
            className="px-3 py-1.5 text-sm border border-[#D7DBD9] rounded-md bg-white text-slate-900 focus:outline-none focus:ring-1 focus:ring-[#D7DBD9]"
          >
            <option value="">All Categories</option>
            {categoryFilterOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.unlisted ? `${opt.label} (not in list)` : opt.label}
              </option>
            ))}
          </select>
          <select
            value={filters.limit}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, limit: parseInt(e.target.value), page: 1 }))
            }
            className="px-3 py-1.5 text-sm border border-[#D7DBD9] rounded-md bg-white text-slate-900 focus:outline-none focus:ring-1 focus:ring-[#D7DBD9]"
          >
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
          </select>
          {/* Assorted-item grouping. Grouped mode is the client's-eye view: one
              "Assorted Yoghurt" line instead of three flavours. */}
          <select
            value={filters.itemGroup || ''}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, itemGroup: e.target.value || undefined, page: 1 }))
            }
            className="px-3 py-1.5 text-sm border border-[#D7DBD9] rounded-md bg-white text-slate-900 focus:outline-none focus:ring-1 focus:ring-[#D7DBD9]"
          >
            <option value="">All items</option>
            <option value="any">In any group</option>
            <option value="none">Not grouped</option>
            {itemGroups.map((g) => (
              <option key={g._id} value={g._id}>
                {g.name}
              </option>
            ))}
          </select>
          <label className="flex items-center gap-1.5 text-sm text-slate-900 cursor-pointer">
            <input
              type="checkbox"
              checked={!!filters.grouped}
              onChange={(e) => {
                setSelectedIds(new Set()); // group rows aren't selectable catalogue ids
                setFilters((prev) => ({ ...prev, grouped: e.target.checked, page: 1 }));
              }}
              className="w-[16px] h-[16px] rounded-[2px] border border-[#BABABA] cursor-pointer"
            />
            Collapse groups
          </label>
          {/* Rows where a vendor invoice stated a conversion that contradicts
              what the row already says. The sync SKIPS such a row rather than
              overwriting good data or failing the invoice, so without a way to
              list them the skip would be silent and nobody would ever reconcile
              the two. */}
          <label
            className="flex items-center gap-1.5 text-sm text-slate-900 cursor-pointer"
            title="Show only items where a vendor invoice stated a unit conversion that contradicts the item's own. The invoice's row was not saved — someone needs to decide which is right."
          >
            <input
              type="checkbox"
              checked={!!filters.hasConversionConflict}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  hasConversionConflict: e.target.checked || undefined,
                  page: 1,
                }))
              }
              className="w-[16px] h-[16px] rounded-[2px] border border-[#BABABA] cursor-pointer"
            />
            Needs review
          </label>
          {/* D29 / MC-5 (USC-BUG-0029): "how would I know when to check the
              conversion breakdown?" A read-only filter over what the Case
              Packaging cell prints. The list of packing alerts itself moved to
              the Review page, 2026-09-23. */}
          <label
            className="flex items-center gap-1.5 text-sm text-slate-900 cursor-pointer"
            title="Show only items whose Case Packaging states nothing: no pack count, no unit size, or neither written as unit conversions."
          >
            <input
              type="checkbox"
              checked={!!filters.needsPackagingReview}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  needsPackagingReview: e.target.checked || undefined,
                  page: 1,
                }))
              }
              className="w-[16px] h-[16px] rounded-[2px] border border-[#BABABA] cursor-pointer"
            />
            Needs packaging review
          </label>
          {/* After DECISIONS D42 a case with a size and no count reads as ONE of
              that size — right for a 25 KG bag, wrong for "Soft Drinks" CASE
              330 ML. The same check the invoice review warns with, over the
              rows already stored. */}
          <label
            className="flex items-center gap-1.5 text-sm text-slate-900 cursor-pointer"
            title="Show only cases, boxes and cartons read as one item that is probably several: a size under 2 kg or 2 L, or a count in the item name, with Units per Case 1 or blank. Enter how many one case holds."
          >
            <input
              type="checkbox"
              checked={!!filters.looksLikeOneItem}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  looksLikeOneItem: e.target.checked || undefined,
                  page: 1,
                }))
              }
              className="w-[16px] h-[16px] rounded-[2px] border border-[#BABABA] cursor-pointer"
            />
            Case size looks like one item
          </label>
          {/* The groups screen lives off the catalogue rather than the sidebar —
              it is an overlay on these rows, not a module of its own. */}
          <button
            type="button"
            onClick={() => navigate('/item-groups')}
            className="text-sm text-blue-700 hover:underline"
          >
            {itemGroups.length ? 'Manage groups →' : 'Set up item groups →'}
          </button>
          <span className="text-sm text-slate-900">
            All prices in <span className="font-semibold">TT$</span>
          </span>
        </div>
        )}
      </div>

      {inLineMode && lineQuotationId && lineNumberParam && (
        <QuotationLineOptionsBanner
          quotationId={lineQuotationId}
          lineNumber={lineNumberParam}
          options={lineOptions}
          error={lineOptionsError}
          loading={loading}
          shownRowIds={(data?.data || []).map((row) => row._id)}
          onShowAllRows={() => setSearchParams({})}
        />
      )}

      {!inLineMode && filters.search && <InvoiceCoverageNote search={filters.search} />}

      {groupNotice && (
        <div className="px-6 pt-3">
          <div className="rounded-md border border-blue-200 bg-blue-50 text-blue-900 px-3 py-2 text-xs flex items-start gap-2">
            <span className="flex-1">{groupNotice}</span>
            <button type="button" className="font-semibold" onClick={() => setGroupNotice(null)}>
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Pricing legend. Ops read TT$280.50 on a vendor invoice, saw TT$315.56 here and
          assumed the catalogue was wrong — the two numbers are different things and
          nothing on the screen said so. The column tooltips already explained it, but a
          tooltip you have to hover is not an explanation. State both rules up front. */}
      <div className="px-6 pt-3">
        <div className="rounded-md border border-blue-200 bg-blue-50 text-blue-900 px-3 py-2 text-xs leading-[18px]">
          <span className="font-semibold">How to read these prices</span>
          <ul className="mt-1 space-y-0.5 list-disc pl-4">
            <li>
              Cost prices are <span className="font-semibold">VAT-inclusive</span>. Where the{' '}
              <span className="font-semibold">VAT</span> column says Yes, {vatRateLabel} is added on top
              of the vendor&apos;s invoice price{vatWorkedExampleClause} VAT is not billed on to the vessels,
              so we carry it as cost.
            </li>
            <li>
              Prices are the vendor&apos;s <span className="font-semibold">list price before any
              discount</span>. Discount schemes can be withdrawn at any time, so the catalogue holds
              the undiscounted price rather than one that may quietly expire.
            </li>
          </ul>
        </div>
      </div>

      {/* Table - compact style matching quotation: 11px fonts, reduced padding */}
      <div className="px-6 py-4" style={{ width: '100%', maxWidth: '100%' }}>
        <div className="border border-[#D7DBD9] rounded-md overflow-auto max-h-[min(70vh,calc(100vh-240px))] min-h-[300px] bg-white">
          {/* The select box and the item name stay put while the other sixteen
              columns scroll (see `frozen-cols-*` in index.css). 56px is the
              first column's own `w-14`, which both its header and its cells
              carry. */}
          <table
            className="w-full border-collapse min-w-[2470px] frozen-cols-2"
            style={{ '--frozen-col-2': '56px', '--frozen-divider': '#D7DBD9' } as React.CSSProperties}
          >
            <thead>
              <tr className="bg-[#F9FAF9] [&>th]:text-xs [&>th]:font-semibold [&>th]:text-slate-600 [&>th]:sticky [&>th]:top-0 [&>th]:z-10 [&>th]:shadow-[0_1px_0_0_#D7DBD9]">
                <th className="box-border w-14 min-w-[56px] py-[3px] px-[8px] bg-[#F9FAF9] border-b border-t border-[#D7DBD9] rounded-tl-md">
                  <div className="flex flex-row justify-center items-center gap-2 h-full">
                    <input
                      type="checkbox"
                      checked={data?.data?.length ? data.data.every(item => selectedIds.has(item._id)) : false}
                      onChange={selectAllOnPage}
                      title="Select all on page"
                      className="w-[18px] h-[18px] rounded-[2px] border border-[#BABABA] cursor-pointer"
                    />
                  </div>
                </th>
                <th className="box-border min-w-[232px] w-[232px] py-[3px] px-[8px] bg-[#F9FAF9] border-b border-t border-[#D7DBD9]">
                  <SortableHeader label="Item Name" column="item_name" count={data?.pagination.total} />
                </th>
                <th className="box-border min-w-[160px] w-[160px] py-[3px] px-[8px] bg-[#F9FAF9] border-b border-t border-[#D7DBD9]">
                  <SortableHeader label="Last Updated" column="last_updated" />
                </th>
                <th className="box-border min-w-[148px] w-[148px] py-[3px] px-[8px] bg-[#F9FAF9] border-b border-t border-[#D7DBD9]">
                  <SortableHeader label="Category" column="category" count={filterOptions.categories.length} />
                </th>
                <th className="box-border min-w-[162px] w-[162px] py-[3px] px-[8px] bg-[#F9FAF9] border-b border-t border-[#D7DBD9]">
                  <SortableHeader label="Sub Category" column="sub_category" />
                </th>
                <th className="box-border min-w-[158px] w-[158px] py-[3px] px-[8px] bg-[#F9FAF9] border-b border-t border-[#D7DBD9]">
                  <SortableHeader label="Source" column="source" count={filterOptions.sources.length} />
                </th>
                <th className="box-border min-w-[150px] w-[150px] py-[3px] px-[8px] bg-[#F9FAF9] border-b border-t border-[#D7DBD9]">
                  <SortableHeader label="Supplier / Vendor" column="supplier" />
                </th>
                <th className="box-border min-w-[120px] w-[120px] py-[3px] px-[8px] bg-[#F9FAF9] border-b border-t border-[#D7DBD9]">
                  <span className="text-xs font-normal leading-[18px] text-slate-900">Invoice #</span>
                </th>
                <th className="box-border min-w-[110px] w-[110px] py-[3px] px-[8px] bg-[#F9FAF9] border-b border-t border-[#D7DBD9]">
                  <span className="text-xs font-normal leading-[18px] text-slate-900">Invoice Date</span>
                </th>
                {/* The `Cost Price (TT$)` selector column stood here. Its four
                    options each printed a figure one of the four columns to the
                    right already prints — see the matching note on the cell. */}
                <th
                  className="box-border min-w-[136px] w-[136px] py-[3px] px-[8px] bg-[#F9FAF9] border-b border-t border-[#D7DBD9]"
                  title="What this row's cost moved from, in the currency it was invoiced in, and the invoice that stated it. One row now carries a supplier's product across every invoice of it, so this is the only place the superseded price is kept."
                >
                  <span className="text-xs font-normal leading-[18px] text-slate-900">Previous Cost</span>
                </th>
                <th
                  className="box-border min-w-[140px] py-[3px] px-[8px] bg-[#F9FAF9] border-b border-t border-[#D7DBD9]"
                  title={`VAT-inclusive cost per case in TT$ (${vatRateLabel} VAT only when applicable for the item)`}
                >
                  <SortableHeader label="Per Case (TT$, w/ VAT)" column="finalPrice" />
                </th>
                <th className="box-border min-w-[86px] w-[86px] py-[3px] px-[8px] bg-[#F9FAF9] border-b border-t border-[#D7DBD9]">
                  <SortableHeader label="Unit" column="unit" />
                </th>
                {/* Prod USC-BUG-0011 put it here, after Unit (0007 first asked for
                    it beside Per Case). The pack sizes behind Per Pack / Per kg
                    were already stored; only the item form showed them. */}
                <th
                  className="box-border min-w-[150px] w-[150px] py-[3px] px-[8px] bg-[#F9FAF9] border-b border-t border-[#D7DBD9]"
                  title="What one case holds, as this item's unit conversions state it — e.g. CASE of 12 x 500 ML"
                >
                  <span className="text-xs font-normal leading-[18px] text-slate-900">Case Packaging</span>
                </th>
                <th
                  className="box-border min-w-[118px] w-[118px] py-[3px] px-[6px] bg-[#F9FAF9] border-b border-t border-[#D7DBD9]"
                  title="VAT-inclusive cost per pack (from case pack size, e.g. one 1kg pack in 10×1kg)"
                >
                  <div className="font-['Public_Sans'] font-medium text-xs leading-[14px] flex flex-col items-center text-center text-primary-600">
                    <span>Per Pack</span>
                    <span className="font-normal text-[#444]">(TT$, w/ VAT)</span>
                  </div>
                </th>
                {/* MC-2 (D28, labels PROVISIONAL O-6): one column for a solid's
                    kg or a liquid's litre, one for its lb or gallon, and the
                    cell prints which. A litre is never a kilogram (D36); an
                    ounce row shows both, at its stated volume else by water (D170.4). */}
                <th
                  className="box-border min-w-[110px] w-[110px] py-[3px] px-[6px] bg-[#F9FAF9] border-b border-t border-[#D7DBD9]"
                  title="VAT-inclusive cost per kg for a solid, or per litre for a liquid. Only an ounce size converts to litres: at the volume the row states, else 1 g = 1 ml."
                >
                  <div className="font-['Public_Sans'] font-medium text-xs leading-[14px] flex flex-col items-center text-center text-primary-600">
                    <span>Per kg / L</span>
                    <span className="font-normal text-[#444]">(TT$, w/ VAT)</span>
                  </div>
                </th>
                <th
                  className="box-border min-w-[110px] w-[110px] py-[3px] px-[6px] bg-[#F9FAF9] border-b border-t border-[#D7DBD9]"
                  title={`VAT-inclusive cost per lb for a solid (1 kg = ${1 / LB_PER_KG} lb), or per gallon for a liquid (1 gallon = ${LITRES_PER_GALLON} L)`}
                >
                  <div className="font-['Public_Sans'] font-medium text-xs leading-[14px] flex flex-col items-center text-center text-primary-600">
                    <span>Per lb / gal</span>
                    <span className="font-normal text-[#444]">(TT$, w/ VAT)</span>
                  </div>
                </th>
                <th className="box-border min-w-[120px] py-[3px] px-[8px] bg-[#F9FAF9] border-b border-t border-[#D7DBD9]">
                  <SortableHeader label="VAT" column="vat_applicable" />
                </th>
                <th className="box-border min-w-[200px] py-[3px] px-[8px] bg-[#F9FAF9] border-b border-t border-[#D7DBD9] rounded-tr-md">
                  <span className="text-xs font-normal leading-[18px] text-slate-900">Keywords</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={18} className="px-4 py-8 text-center text-xs leading-[16px] text-slate-900 bg-white border-b border-[#D7DBD9] rounded-b-md">
                    <LoadingState text="Loading master catalogue..." />
                  </td>
                </tr>
              ) : !data?.data || data.data.length === 0 ? (
                <tr>
                  <td colSpan={18} className="px-4 py-8 text-center text-xs leading-[16px] text-slate-900 bg-white border-b border-[#D7DBD9] rounded-b-md">
                    No items found
                  </td>
                </tr>
              ) : (
                data.data.map((item, index) => {
                  const isLastRow = index === data.data.length - 1;
                  const pricingDetails =
                    item.meta_data && typeof item.meta_data === 'object'
                      ? (item.meta_data.pricingDetails as Record<string, unknown> | undefined)
                      : undefined;
                  // The shared "pack read off the item name" derivation
                  // (`features/catalogue/costPriceTiers.altPricingFor`) — the
                  // vendor-invoice sync preview card reads it the same way,
                  // off its own `priceFacts`, so the two cannot drift.
                  const altPricing = altPricingFor(item, usdToTtd, pricingDetails);
                  // One computation for the four money cells below, rather than
                  // four calls that each re-derive the case figure and re-parse
                  // the pack size — on every render and every sort, for every
                  // visible row.
                  const costTiers = costTiersFor(item, altPricing);
                  const caseUnitSuffix = baseUnitSuffix(item);
                  // The row that priced the quotation line this screen was opened
                  // from. Every cell paints its own background, so the tint goes
                  // on the cells; the left rule marks it for anyone who cannot
                  // tell the tint apart, and the rule under it separates the row
                  // in use from the other options (it is always pinned first).
                  const isUsedRow =
                    inLineMode && !!lineOptions?.usedRow.rowId && item._id === lineOptions.usedRow.rowId;
                  return (
                  <tr
                    key={item._id}
                    className={`bg-white hover:bg-[#F9FAF9] transition-colors ${selectedIds.has(item._id) ? 'bg-blue-50/50' : ''} ${
                      isUsedRow
                        ? '[&>td]:!bg-cyan-50 [&>td:first-child]:!border-l-4 [&>td:first-child]:!border-l-cyan-600 [&>td]:!border-b-2 [&>td]:!border-b-cyan-600'
                        : ''
                    }`}
                    data-priced-this-line={isUsedRow ? 'true' : undefined}
                  >
                    <td className={`box-border w-14 min-w-[56px] py-[4px] px-[8px] bg-white border-b border-[#D7DBD9] ${isLastRow ? 'rounded-bl-md' : ''}`}>
                      <div className="flex justify-center items-center">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(item._id)}
                          onChange={() => toggleSelect(item._id)}
                          // A collapsed group row is not a catalogue row — selecting it
                          // would send a synthetic "group:<id>" to endpoints expecting ids.
                          disabled={!!item.isGroup}
                          className="w-[18px] h-[18px] rounded-[2px] border border-[#BABABA] cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                        />
                      </div>
                    </td>
                    <td className="box-border min-w-[232px] py-[4px] px-[8px] bg-white border-b border-[#D7DBD9] min-w-0 overflow-hidden">
                      <div className="flex items-center gap-2 min-w-0">
                        {/* A PriceSmart row's item code, in brackets: the receipt prints the
                            code, so it is how the row is recognised (vendorItemCode). */}
                        <span
                          className="text-xs font-normal leading-[16px] text-slate-900 truncate min-w-0"
                          title={item.vendorItemCode ? `${item.item_name} (${item.vendorItemCode})` : item.item_name}
                        >
                          {item.item_name}
                          {item.vendorItemCode && (
                            <span className="text-slate-500" data-testid="catalogue-item-code"> ({item.vendorItemCode})</span>
                          )}
                        </span>
                        {isUsedRow && (
                          <span
                            className="flex-shrink-0 inline-flex items-center gap-1 rounded-md bg-cyan-700 text-white text-xs font-semibold px-1.5 py-0.5 leading-none"
                            title={
                              lineOptions?.usedRow.status === 'merged'
                                ? `The row quotation line ${lineOptions?.line.lineNumber} was priced from has since been merged into this row`
                                : `Quotation line ${lineOptions?.line.lineNumber} is priced from this row`
                            }
                            data-testid="catalogue-row-in-use"
                          >
                            <svg width="9" height="9" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden="true">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M1.5 5.5l2.5 2.5 4.5-6" />
                            </svg>
                            In use
                          </span>
                        )}
                        {/* The flag alone (D170.6): the pricing rule reads it, and unticking leaves the tag. */}
                        {item.is_fresh === true && (
                          <span
                            className="flex-shrink-0 inline-flex items-center rounded-full bg-green-50 border border-green-200 text-green-700 text-xs font-semibold px-1.5 py-0.5 leading-none"
                            title="Fresh item — priced monthly by hand; a bill only proposes its price"
                          >
                            Fresh
                          </span>
                        )}
                        {/* A vendor invoice stated a conversion this row's own
                            conversions contradict. The invoice's row was NOT
                            saved — the badge is how somebody finds out, and the
                            tooltip carries the sentence naming both numbers so
                            the decision can be made without opening the item. */}
                        {item.hasConversionConflict && (
                          <span
                            className="flex-shrink-0 inline-flex items-center rounded-full bg-amber-50 border border-amber-300 text-amber-800 text-xs font-semibold px-1.5 py-0.5 leading-none"
                            title={`A vendor invoice stated a unit conversion that contradicts this item's own, and was not saved:\n\n${(item.conversionConflicts || [])
                              .map((c) => `• ${c.message}`)
                              .join('\n')}`}
                          >
                            ⚠ Conversion
                          </span>
                        )}
                        {/* Assorted-item grouping: which client-facing line this variant rolls into. */}
                        {item.isGroup ? (
                          <span
                            className="flex-shrink-0 inline-flex items-center rounded-full bg-indigo-50 border border-indigo-200 text-indigo-800 text-xs font-semibold px-1.5 py-0.5 leading-none"
                            title={`Collapsed from ${item.memberCount} vendor line(s): ${(item.members || [])
                              .map((m) => m.item_name)
                              .join(', ')}`}
                          >
                            Group · {item.memberCount}
                          </span>
                        ) : item.group ? (
                          <span
                            className="flex-shrink-0 inline-flex items-center rounded-full bg-indigo-50 border border-indigo-200 text-indigo-800 text-xs font-semibold px-1.5 py-0.5 leading-none"
                            // The chip is a name, and a name may belong to two
                            // groups; name the slug so the row says which one.
                            title={`Sold to clients as "${item.group.name}"${
                              isDuplicateGroupName(item.group.name)
                                ? ` (one of several groups with that name — this is "${item.group.slug}")`
                                : ''
                            }${item.groupAssignment?.uomWarning ? ` — ⚠ ${item.groupAssignment.uomWarning}` : ''}`}
                          >
                            {item.group.name}
                            {item.groupAssignment?.uomWarning ? ' ⚠' : ''}
                          </span>
                        ) : item.suggestedGroup ? (
                          <span
                            className="flex-shrink-0 inline-flex items-center rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-xs font-semibold px-1.5 py-0.5 leading-none"
                            title={`Suggested group "${item.suggestedGroup.name}"${
                              isDuplicateGroupName(item.suggestedGroup.name)
                                ? ` (one of several groups with that name — this suggestion targets "${item.suggestedGroup.slug}")`
                                : ''
                            } — accept or reject it on the Assorted Item Groups screen`}
                          >
                            ? {item.suggestedGroup.name}
                          </span>
                        ) : null}
                        {/* A collapsed group row has no catalogue document to edit or delete. */}
                        {!item.isGroup && (
                          <>
                            <button
                              type="button"
                              onClick={() => setEditItemOpen(item)}
                              className="p-1 text-slate-500 hover:text-slate-900"
                              title="Edit item"
                            >
                              <EditIcon className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteItem(item)}
                              disabled={deletingItemId === item._id}
                              className="p-1 text-slate-500 hover:text-red-600 disabled:opacity-40"
                              title="Delete item"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="box-border min-w-[160px] py-[3px] px-[8px] bg-white border-b border-[#D7DBD9] text-xs leading-[16px] text-slate-900 whitespace-nowrap">
                      {formatDate(item.last_updated)}
                    </td>
                    <td className="box-border min-w-[148px] py-[3px] px-[8px] bg-white border-b border-[#D7DBD9] text-xs leading-[16px] text-slate-900">
                      {categoryLabelFor(item.category)}
                    </td>
                    <td className="box-border min-w-[162px] py-[3px] px-[8px] bg-white border-b border-[#D7DBD9] text-xs leading-[16px] text-slate-900">
                      {item.sub_category || '—'}
                    </td>
                    <td className="box-border min-w-[158px] py-[3px] px-[8px] bg-white border-b border-[#D7DBD9] text-xs leading-[16px] text-slate-900">
                      {formatSourceLabel(item.source)}
                    </td>
                    <td className="box-border min-w-[150px] py-[3px] px-[8px] bg-white border-b border-[#D7DBD9] text-xs leading-[16px] text-slate-900">
                      <span className="truncate block max-w-[140px]" title={item.supplier || undefined}>
                        {item.supplier || '—'}
                      </span>
                    </td>
                    <td className="box-border min-w-[120px] py-[3px] px-[8px] bg-white border-b border-[#D7DBD9] text-xs leading-[16px] text-slate-900">
                      {item.meta_data?.invoiceNumber ? (
                        <InvoiceRef id={pricingInvoiceId(item)}>{item.meta_data.invoiceNumber}</InvoiceRef>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="box-border min-w-[110px] py-[3px] px-[8px] bg-white border-b border-[#D7DBD9] text-xs leading-[16px] text-slate-900 whitespace-nowrap">
                      {item.meta_data?.invoiceDate
                        ? /* dd/mm/yyyy, like every other date in this grid (item 54) — the
                             raw stored string ("2026-10-01") was printed as-is before. A
                             date the owner cannot parse still shows the original text
                             rather than going blank (missing is not the same as unreadable). */
                          fmtDate(item.meta_data.invoiceDate, String(item.meta_data.invoiceDate))
                        : item.source === 'price_list' && item.meta_data?.priceListUploadedAt
                          ? `Price list uploaded on ${fmtDate(item.meta_data.priceListUploadedAt)}`
                          : '—'}
                    </td>
                    <td className="box-border min-w-[136px] py-[3px] px-[8px] bg-white border-b border-[#D7DBD9] text-xs leading-[16px] text-[#666666] whitespace-nowrap">
                      <PreviousCostCell item={item} />
                    </td>
                    {/*
                      ONE case column, not two.
                      `Cost Price (TT$)` with its unit selector stood here and
                      printed `costTiers[costPriceUnit]` — which at its default
                      "Per Case" is this cell's figure, and at the other three is
                      the Per Pack / Per kg / Per lb cell three columns along. It
                      could never show anything the row did not already show, and
                      the owner read the repetition off the screen: "they are
                      mostly same". Removed 2026-09-08 by owner decision.

                      This cell used to be `storedCaseTtd(item) ?? formatPrice(...)`
                      and agreed with the old Cost Price column only while the
                      stored tier was there: the fallback ran through a local
                      `toTtd` asking `currency === 'TTD'`, a live duplicate of the
                      rule `catalogueRowIsTtd` owns. `MasterCatalogue.currency` is
                      `default: null`, so a LEGACY vendor-invoice row was multiplied
                      by the rate and this column printed 6.5x the supplier's price
                      beside a Cost Price column printing it correctly. Every
                      collapsed group row had it too — `getGroupedItems` synthesised
                      a TT$ price and no `currency` field at all; it now states
                      `currency: 'TTD'` and a VAT-inclusive `finalPrice`, which
                      `costPriceTiersTtd` reads as the row's case figure (#4,
                      D117.4). It is the tier itself now, and the page holds no
                      currency rule.
                    */}
                    <td
                      className="box-border min-w-[154px] py-[3px] px-[8px] bg-white border-b border-[#D7DBD9] text-xs leading-[16px] text-blue-700 font-semibold"
                      title={
                        caseUnitSuffix
                          // The number is right; the heading was not. A
                          // catch-weight line is billed per pound, so this row's
                          // price is a per-pound figure and no case price exists
                          // to show instead.
                          ? `VAT-inclusive cost per ${caseUnitSuffix} — this row's own unit. It states no case pack, so there is no case price to show.`
                          : 'VAT-inclusive cost per case (item base unit)'
                      }
                    >
                      {costTiers.case == null ? (
                        <span className="text-[#CCC]">—</span>
                      ) : (
                        <>
                          {formatTtd(costTiers.case)}
                          {/* Moved here with the column it used to sit in. Without
                              it this cell says "Per Case" over a per-pound figure,
                              which is the heading the owner reported. */}
                          {caseUnitSuffix && (
                            <span className="text-[#888] font-normal"> / {caseUnitSuffix}</span>
                          )}
                        </>
                      )}
                    </td>
                    <td className="box-border min-w-[86px] py-[3px] px-[8px] bg-white border-b border-[#D7DBD9] text-xs leading-[16px] text-slate-900">
                      {item.unit || '—'}
                    </td>
                    <td className="box-border min-w-[150px] py-[3px] px-[8px] bg-white border-b border-[#D7DBD9] text-xs leading-[16px] text-slate-900">
                      {/* Display only — the tiers to the right price from their own
                          readings. Nothing stated is never a guessed pack: the cell
                          names what is missing (MC-3, the server's `packagingGap`),
                          and a row without one (a collapsed group) keeps "—". */}
                      {describePackaging(conversionsForItem(item), item.unit) ||
                        (item.packagingGap ? (
                          <span className="text-[#888]">{item.packagingGap}</span>
                        ) : (
                          <span className="text-[#CCC]">—</span>
                        ))}
                    </td>
                    <td
                      className="box-border min-w-[118px] py-[4px] px-[4px] bg-[#FAFCFB] border-b border-[#D7DBD9] align-middle"
                      title={
                        // A blank cell says why it is blank (NEW-8, USC-BUG-0013 /
                        // 0029) rather than describe a figure it does not show.
                        perPackBlankReason(item, costTiers) ??
                        // The name-reading's own explanation belongs only to a
                        // figure the name-reading produced.
                        (costTiers.packBasis === 'item_name'
                          ? altPricing?.basisHint || 'VAT-inclusive per pack, read from the item name'
                          : 'VAT-inclusive per pack, from the pack size this row states')
                      }
                    >
                      <div className="font-['Public_Sans'] font-normal text-xs leading-[14px] flex flex-col items-center text-center text-slate-900 min-h-[32px] justify-center">
                        {(() => {
                          const v = costTiers.pack;
                          return v != null ? (
                            <>
                              <span>{formatTtd(v)}</span>
                              {costTiers.packBasis === 'item_name' && altPricing?.innerUnitLabel && (
                                <span className="text-xs text-[#888] max-w-[110px] truncate">
                                  {altPricing.innerUnitLabel}
                                </span>
                              )}
                            </>
                          ) : (
                            <span className="text-[#CCC]">—</span>
                          );
                        })()}
                      </div>
                    </td>
                    <td className="box-border min-w-[110px] py-[4px] px-[4px] bg-[#FAFCFB] border-b border-[#D7DBD9] align-middle">
                      <div className="font-['Public_Sans'] font-normal text-xs leading-[14px] flex flex-col items-center text-center text-slate-900 min-h-[32px] justify-center">
                        {measureFigures([[costTiers.kg, 'kg'], [costTiers.l, 'L']])}
                      </div>
                    </td>
                    <td className="box-border min-w-[110px] py-[4px] px-[4px] bg-[#FAFCFB] border-b border-[#D7DBD9] align-middle">
                      <div className="font-['Public_Sans'] font-normal text-xs leading-[14px] flex flex-col items-center text-center text-slate-900 min-h-[32px] justify-center">
                        {measureFigures([[costTiers.lb, 'lb'], [costTiers.gal, 'gal']])}
                      </div>
                    </td>
                    <td className="box-border min-w-[120px] py-[3px] px-[8px] bg-white border-b border-[#D7DBD9] text-xs leading-[16px] text-slate-900">
                      {item.vat_applicable ? (
                        <span className="inline-block px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium">Yes</span>
                      ) : item.vat_applicable === false ? (
                        <span className="inline-block px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-medium">No</span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className={`box-border min-w-[200px] py-[3px] px-[8px] bg-white border-b border-[#D7DBD9] ${isLastRow ? 'rounded-br-md' : ''}`}>
                      {editingKeywords[item._id] ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={keywordInputs[item._id] || ''}
                            onChange={(e) => setKeywordInputs(prev => ({ ...prev, [item._id]: e.target.value }))}
                            placeholder="Keywords, comma-separated"
                            className="flex-1 px-2 py-1 text-xs border border-[#D7DBD9] rounded focus:outline-none focus:ring-1 focus:ring-[#D7DBD9] text-slate-900"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSaveKeywords(item._id);
                              else if (e.key === 'Escape') handleCancelEditKeywords(item._id);
                            }}
                            autoFocus
                          />
                          <button type="button" onClick={() => handleSaveKeywords(item._id)} disabled={savingKeywords[item._id]} className="p-1 text-blue-600 hover:text-blue-700 disabled:opacity-50" title="Save">
                            <SaveIcon className="w-4 h-4" />
                          </button>
                          <button type="button" onClick={() => handleCancelEditKeywords(item._id)} disabled={savingKeywords[item._id]} className="p-1 text-red-600 hover:text-red-700 disabled:opacity-50" title="Cancel">
                            <CancelIcon className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 group">
                          <div className="flex-1">
                            {item.keywords?.length ? (
                              <div className="flex flex-wrap gap-1">
                                {item.keywords.map((keyword, idx) => (
                                  <span key={idx} className="inline-block px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                                    {keyword}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-slate-500 text-xs">No keywords</span>
                            )}
                          </div>
                          <button type="button" onClick={() => handleEditKeywords(item._id, item.keywords)} className="p-1 text-slate-500 hover:text-slate-900 opacity-0 group-hover:opacity-100 transition" title="Edit keywords">
                            <EditIcon className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {data && data.pagination.totalPages > 1 && (
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200">
          <div className="text-sm text-slate-600">
            Showing {(filters.page - 1) * filters.limit + 1} to{' '}
            {Math.min(filters.page * filters.limit, data.pagination.total)} of {data.pagination.total.toLocaleString()}{' '}
            items
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => handlePageChange(filters.page - 1)}
              disabled={filters.page === 1}
              className="p-2 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
              title="Previous page"
            >
              <ChevronLeftIcon className="w-5 h-5" />
            </button>

            {/* Page numbers */}
            <div className="flex gap-1">
              {Array.from({ length: Math.min(5, data.pagination.totalPages) }, (_, i) => {
                let pageNum: number;
                if (data.pagination.totalPages <= 5) {
                  pageNum = i + 1;
                } else if (filters.page <= 3) {
                  pageNum = i + 1;
                } else if (filters.page >= data.pagination.totalPages - 2) {
                  pageNum = data.pagination.totalPages - 4 + i;
                } else {
                  pageNum = filters.page - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`px-3 py-1 rounded-lg transition ${
                      filters.page === pageNum
                        ? 'bg-blue-500 text-white'
                        : 'border border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => handlePageChange(filters.page + 1)}
              disabled={filters.page === data.pagination.totalPages}
              className="p-2 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
              title="Next page"
            >
              <ChevronRightIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Upload price list → master catalogue (selective import) */}
      <Modal
        isOpen={uploadModalOpen}
        onClose={() => {
          if (!uploadingCatalogue && !previewingCatalogue) resetUploadModal();
        }}
        title="Upload price list"
        maxWidthClass={uploadPreviewRows ? 'max-w-3xl' : 'max-w-md'}
      >
        {uploadError && (
          <p role="alert" className="mb-3 px-3 py-2 text-sm rounded-md bg-red-50 text-red-800 border border-red-200">
            {uploadError}
          </p>
        )}
        {!uploadPreviewRows ? (
          <>
            <p className="text-sm text-slate-700 mb-3">
              Upload an Excel, EML, or image file. You will be able to review the parsed entries
              and choose which ones to add — duplicates already in the catalogue are flagged.
            </p>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="block text-sm font-medium text-slate-900 mb-1">Label (optional)</label>
                <input
                  type="text"
                  value={uploadListName}
                  onChange={(e) => setUploadListName(e.target.value)}
                  placeholder="e.g. Marine supplies 2025"
                  className="w-full px-3 py-2 border border-[#D7DBD9] rounded-md text-sm"
                  disabled={previewingCatalogue}
                />
              </div>
              {uploadSupplierField}
            </div>
            <input
              ref={uploadFileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv,.eml,image/*"
              className="hidden"
              onChange={(e) => setUploadFile(e.target.files?.[0] ?? null)}
            />
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <button
                type="button"
                onClick={() => uploadFileInputRef.current?.click()}
                disabled={previewingCatalogue}
                className="px-3 py-2 text-sm border border-blue-700 rounded-md text-blue-800 bg-blue-50 hover:bg-blue-100 disabled:opacity-50"
              >
                Choose file
              </button>
              <span className="text-sm text-slate-900 truncate max-w-[220px]" title={uploadFile?.name}>
                {uploadFile ? uploadFile.name : 'No file selected'}
              </span>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={resetUploadModal}
                className="px-4 py-2 border border-slate-300 rounded-lg"
                disabled={previewingCatalogue}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handlePreviewCatalogueImport}
                disabled={!uploadFile || previewingCatalogue}
                className="px-4 py-2 bg-primary-500 text-white rounded-md disabled:opacity-50"
              >
                {previewingCatalogue ? 'Reading file...' : 'Preview entries'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="mb-3 max-w-sm">{uploadSupplierField}</div>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <p className="text-sm text-slate-700">
                Select the entries to add to the Master Catalog ({uploadSelectedRows.size} of{' '}
                {uploadPreviewRows.length} selected). Prices are in TT$.
              </p>
              <div className="flex gap-2 text-xs">
                <button
                  type="button"
                  onClick={() => setUploadSelectedRows(new Set(uploadPreviewRows.map(r => r.index)))}
                  className="px-2 py-1 border border-[#D7DBD9] rounded text-primary-500 hover:bg-blue-50"
                >
                  Select all
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setUploadSelectedRows(new Set(uploadPreviewRows.filter(r => !r.duplicate).map(r => r.index)))
                  }
                  className="px-2 py-1 border border-[#D7DBD9] rounded text-primary-500 hover:bg-blue-50"
                >
                  New items only
                </button>
                <button
                  type="button"
                  onClick={() => setUploadSelectedRows(new Set())}
                  className="px-2 py-1 border border-[#D7DBD9] rounded text-slate-500 hover:bg-slate-50"
                >
                  Clear
                </button>
              </div>
            </div>
            <div className="border border-[#D7DBD9] rounded-md overflow-auto max-h-[50vh] mb-4">
              <table className="w-full text-sm border-collapse text-sm">
                <thead>
                  <tr className="bg-[#F9FAF9] text-left text-xs text-slate-900 [&>th]:sticky [&>th]:top-0 [&>th]:bg-[#F9FAF9] [&>th]:border-b [&>th]:border-[#D7DBD9]">
                    <th className="px-2 py-1.5 w-10"></th>
                    <th className="px-2 py-1.5">Description</th>
                    <th className="px-2 py-1.5 w-28 text-right">Price (TT$)</th>
                    <th className="px-2 py-1.5 w-14 text-center">VAT</th>
                    <th className="px-2 py-1.5 w-48">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {uploadPreviewRows.map((row) => (
                    <tr
                      key={row.index}
                      onClick={() => toggleUploadRow(row.index)}
                      className={`border-b border-[#D7DBD9]/60 cursor-pointer ${
                        uploadSelectedRows.has(row.index) ? 'bg-blue-50/60' : 'bg-white hover:bg-[#F9FAF9]'
                      }`}
                    >
                      <td className="px-2 py-1 text-center">
                        <input
                          type="checkbox"
                          checked={uploadSelectedRows.has(row.index)}
                          onChange={() => toggleUploadRow(row.index)}
                          onClick={(e) => e.stopPropagation()}
                          className="w-4 h-4 cursor-pointer"
                        />
                      </td>
                      <td className="px-2 py-1 text-xs text-slate-900">
                        {row.description}
                        {/* What the file said beside the name, so the preview shows the
                            columns it read (USC-BUG-0020). */}
                        {[row.packaging, row.category, row.sub_category, row.vendorName].some(Boolean) && (
                          <div className="text-xs text-slate-500">
                            {[
                              row.packaging && `Unit: ${row.packaging}`,
                              row.category && `Category: ${row.category}`,
                              row.sub_category && `Sub: ${row.sub_category}`,
                              row.vendorName && `Vendor: ${row.vendorName}`,
                            ].filter(Boolean).join(' · ')}
                          </div>
                        )}
                        {row.vendorNotInList && (
                          <div role="alert" className="text-xs text-red-600">
                            Vendor not in vendor list - this row will not be imported.
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-1 text-xs text-slate-900 text-right">
                        {/* The file's own price, shown as printed (item 54: this was a
                            bare `TT$${x.toFixed(2)}` with no thousands separator). */}
                        {formatCurrency(row.unit_price, 'TTD', { ceil: false })}
                      </td>
                      <td className="px-2 py-1 text-xs text-center">{row.vat ? 'Yes' : 'No'}</td>
                      <td className="px-2 py-1 text-xs">
                        {row.duplicate ? (
                          <span
                            className="inline-block px-2 py-0.5 bg-amber-100 text-amber-800 rounded font-medium"
                            title={
                              row.existingPrice != null
                                ? `Already in catalogue at ${formatStoredTtd(row.existingPrice, row.existingCurrency, row.existingSource)}${row.existingSupplier ? ` (${row.existingSupplier})` : ''}`
                                : 'An item with this name already exists'
                            }
                          >
                            Already in catalog
                          </span>
                        ) : (
                          <span className="inline-block px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded font-medium">
                            New
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-between gap-2">
              <button
                type="button"
                onClick={() => {
                  setUploadPreviewRows(null);
                  setUploadSelectedRows(new Set());
                }}
                className="px-4 py-2 border border-slate-300 rounded-lg"
                disabled={uploadingCatalogue}
              >
                Back
              </button>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={resetUploadModal}
                  className="px-4 py-2 border border-slate-300 rounded-lg"
                  disabled={uploadingCatalogue}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleCommitCatalogueImport}
                  disabled={uploadSelectedRows.size === 0 || uploadingCatalogue}
                  className="px-4 py-2 bg-primary-500 text-white rounded-md disabled:opacity-50"
                >
                  {uploadingCatalogue
                    ? 'Importing...'
                    : `Import ${uploadSelectedRows.size} selected`}
                </button>
              </div>
            </div>
          </>
        )}
      </Modal>

      {/* Add New Price List modal – design spec */}
      {createPriceListOpen && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 backdrop-blur-[2px] p-4 modal-overlay-safe"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setCreatePriceListOpen(false);
              setCreatePriceListName('');
              setMarkupPercent(5);
            }
          }}
        >
          <div
            className="relative w-[432px] min-h-[303px] bg-white rounded-lg shadow-xl flex flex-col"
            style={{ background: '#FFFFFF', borderRadius: 8 }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 pt-[23px] pb-0" style={{ paddingLeft: 24, paddingRight: 24, paddingTop: 23 }}>
              <h2 className="text-base font-semibold leading-6 text-slate-900">
                Add New Price List
              </h2>
              <button
                type="button"
                onClick={() => { setCreatePriceListOpen(false); setCreatePriceListName(''); setMarkupPercent(5); }}
                aria-label="Close"
                className="flex items-center justify-center w-6 h-6 rounded bg-[#D9D9D9] text-slate-400 hover:opacity-90"
              >
                <span className="text-lg leading-none">×</span>
              </button>
            </div>

            {/* Form */}
            <div className="flex flex-col flex-1 px-6 pb-6" style={{ paddingLeft: 24, paddingRight: 24 }}>
              {/* Price List Name */}
              <div className="mb-5" style={{ marginTop: 21 }}>
                <label className="block font-medium text-slate-900 mb-1.5" style={{ fontFamily: "'Public Sans', sans-serif", fontWeight: 500, fontSize: 14, lineHeight: '22px' }}>
                  Price List Name
                </label>
                <input
                  value={createPriceListName}
                  onChange={e => setCreatePriceListName(e.target.value)}
                  placeholder="Enter Name"
                  className="w-full h-10 px-3 border rounded-md box-border text-base leading-6 placeholder:text-slate-400 text-slate-900 focus:outline-none focus:ring-1 focus:ring-[#D7DBD9]"
                  style={{ border: '1px solid #D7DBD9', borderRadius: 6 }}
                />
              </div>

              {/* ADD button */}
              <button
                type="button"
                onClick={handleCreatePriceList}
                disabled={!createPriceListName.trim() || creatingPriceList}
                className="w-full h-10 flex items-center justify-center rounded-lg font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: '#0A6AAC', borderRadius: 8, fontFamily: "'Public Sans', sans-serif", fontWeight: 600, fontSize: 16, lineHeight: '24px' }}
              >
                {creatingPriceList ? 'Creating...' : 'ADD'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign selected catalogue rows to an assorted-item group */}
      <Modal
        isOpen={assignGroupOpen}
        onClose={() => setAssignGroupOpen(false)}
        title="Group selected items"
        maxWidthClass="max-w-lg"
      >
        <div className="space-y-4 text-xs">
          <p className="text-slate-600">
            {selectedIds.size} item(s) selected. They keep their own rows, costs and suppliers — the group
            is what the client sees and orders as one line.
          </p>
          <label className="block">
            <span className="font-semibold text-slate-900">Group</span>
            <select
              value={assignGroupId}
              onChange={(e) => setAssignGroupId(e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-[#D7DBD9] rounded-md bg-white"
            >
              {itemGroups.map((g) => (
                <option key={g._id} value={g._id}>
                  {g.name}
                  {/* Two groups may share a name. The slug is unique, so it is
                      what makes the options tell each other apart. */}
                  {isDuplicateGroupName(g.name) ? ` · ${g.slug}` : ''} ({g.memberCount ?? 0} member
                  {(g.memberCount ?? 0) === 1 ? '' : 's'})
                </option>
              ))}
            </select>
          </label>
          {isDuplicateGroupName(itemGroups.find((g) => g._id === assignGroupId)?.name) && (
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
              ⚠ More than one group is called "{itemGroups.find((g) => g._id === assignGroupId)?.name}". The name after
              the dot is the slug — it is the only thing that tells them apart.
            </p>
          )}
          <p className="text-xs text-slate-500">
            Assigning by hand locks the choice, so re-uploading the same vendor invoice will not undo it.
            Manage the groups themselves on the Assorted Item Groups screen.
          </p>
        </div>
        <div className="flex justify-between gap-2 mt-5">
          <button
            type="button"
            onClick={() => handleAssignGroup(true)}
            disabled={assigningGroup}
            className="px-4 py-2 border border-slate-300 rounded-lg text-xs font-semibold text-red-700 disabled:opacity-50"
            title="Take the selected items out of whatever group they are in"
          >
            REMOVE FROM GROUP
          </button>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setAssignGroupOpen(false)}
              className="px-4 py-2 border border-slate-300 rounded-lg text-xs font-semibold"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => handleAssignGroup(false)}
              disabled={assigningGroup || !assignGroupId}
              className="px-4 py-2 rounded-md bg-primary-500 text-white text-xs font-semibold disabled:opacity-50"
            >
              {assigningGroup ? 'Saving…' : 'ADD TO GROUP'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Bulk keyword add and remove on the ticked rows (QG-4) */}
      <Modal isOpen={keywordsOpen} onClose={closeBulkKeywords} title="Keywords on selected items" maxWidthClass="max-w-lg">
        <div className="space-y-4 text-xs">
          <p className="text-slate-600">
            {selectedIds.size} item(s) selected. A keyword names the kind of product, such as "instant noodles": an RFQ
            line that asks for exactly that is quoted from the most recent price among every item filed under it, and
            each one is listed on the quotation line. Other keywords on these items stay as they are.
          </p>
          <label className="block">
            <span className="font-semibold text-slate-900">Add keywords</span>
            <input
              aria-label="Add keywords"
              value={keywordsToAdd}
              onChange={(e) => setKeywordsToAdd(e.target.value)}
              placeholder="instant noodles, pantry"
              className="mt-1 w-full px-3 py-2 border border-[#D7DBD9] rounded-md bg-white"
            />
          </label>
          <label className="block">
            <span className="font-semibold text-slate-900">Remove keywords</span>
            <input
              aria-label="Remove keywords"
              value={keywordsToRemove}
              onChange={(e) => setKeywordsToRemove(e.target.value)}
              placeholder="Separate several with commas"
              className="mt-1 w-full px-3 py-2 border border-[#D7DBD9] rounded-md bg-white"
            />
          </label>
          {keywordsError && (
            <p role="alert" className="text-xs text-red-800 bg-red-50 border border-red-200 rounded px-2 py-1">
              {keywordsError}
            </p>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button
            type="button"
            onClick={closeBulkKeywords}
            className="px-4 py-2 border border-slate-300 rounded-lg text-xs font-semibold"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleBulkKeywords}
            disabled={savingBulkKeywords || (!keywordsToAdd.trim() && !keywordsToRemove.trim())}
            className="px-4 py-2 rounded-md bg-primary-500 text-white text-xs font-semibold disabled:opacity-50"
          >
            {savingBulkKeywords ? 'Saving…' : `APPLY TO ${selectedIds.size} ITEMS`}
          </button>
        </div>
      </Modal>

      {/* Add item modal */}
      <Modal isOpen={addItemOpen} onClose={() => setAddItemOpen(false)} title="Add item" maxWidthClass="max-w-md">
        {/*
          `key` on the mount, not a seed effect: the form seeds itself once from
          lazy initial state, so a fresh dialog needs a fresh instance. Without
          it, closing and reopening would show whatever was last typed.
        */}
        {addItemOpen && (
          <CatalogueItemForm
            key="mc-add"
            mode={{ kind: 'create' }}
            onCancel={() => setAddItemOpen(false)}
            onSaved={() => {
              setAddItemOpen(false);
              fetchItems();
            }}
          />
        )}
      </Modal>

      {/* Edit item modal */}
      <Modal isOpen={!!editItemOpen} onClose={() => setEditItemOpen(null)} title="Edit item" maxWidthClass="max-w-md">
        {editItemOpen && (
          <CatalogueItemForm
            key={editItemOpen._id}
            mode={{ kind: 'edit', item: editItemOpen }}
            onCancel={() => setEditItemOpen(null)}
            onSaved={() => {
              setEditItemOpen(null);
              fetchItems();
            }}
          />
        )}
      </Modal>
    </div>
  );
}
